import React, { useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { Sparkles, ArrowRight, RotateCcw, Leaf, Share2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { QUIZ_QUESTIONS, QUIZ_RESULT_NOTES, REMEDY_GROUPS } from "@/lib/content";
import { PHONE, INSTAGRAM_HANDLE } from "@/lib/site";
import { EASE, Reveal } from "@/lib/motion";
import RemedyShareCard from "@/components/RemedyShareCard";

const TOTAL_STEPS = QUIZ_QUESTIONS.length;

function computeResult(answers) {
  const counts = {};
  answers.forEach((tag) => {
    counts[tag] = (counts[tag] || 0) + 1;
  });
  let winner = answers[0];
  let best = 0;
  REMEDY_GROUPS.forEach((g) => {
    const score = counts[g.id] || 0;
    if (score > best) {
      best = score;
      winner = g.id;
    }
  });
  return REMEDY_GROUPS.find((g) => g.id === winner) || REMEDY_GROUPS[0];
}

export default function RemedyQuiz({ onBookNow }) {
  const [step, setStep] = useState(-1);
  const [answers, setAnswers] = useState([]);
  const [isSharing, setIsSharing] = useState(false);
  const shareCardRef = useRef(null);

  const start = () => {
    setAnswers([]);
    setStep(0);
  };

  const pick = (tag) => {
    const next = [...answers, tag];
    setAnswers(next);
    if (step + 1 < TOTAL_STEPS) {
      setStep(step + 1);
    } else {
      setStep(TOTAL_STEPS);
    }
  };

  const retake = () => {
    setAnswers([]);
    setStep(0);
  };

  const result = step === TOTAL_STEPS ? computeResult(answers) : null;
  const topRemedy = result ? result.remedies[0] : null;

  const handleShare = async () => {
    if (!shareCardRef.current || !topRemedy) return;
    setIsSharing(true);
    try {
      const dataUrl = await toPng(shareCardRef.current, { pixelRatio: 3, cacheBust: true });
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], `bloom-and-heal-${result.id}.png`, { type: "image/png" });

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "My Bach Flower Remedy",
          text: `My remedy from Bloom & Heal: ${topRemedy.name}`,
        });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `bloom-and-heal-${result.id}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success("Saved! Upload it to your Instagram Story or WhatsApp Status.");
      }
    } catch (err) {
      if (err?.name !== "AbortError") {
        toast.error("Couldn't create the image. Please try again.");
      }
    } finally {
      setIsSharing(false);
    }
  };

  return (
    <section id="quiz" className="py-20 sm:py-28 bg-[#F6F2EB]/50 relative overflow-hidden">
      <div className="absolute top-0 -left-32 w-96 h-96 rounded-full bg-[#F9EAE8]/60 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 -right-32 w-96 h-96 rounded-full bg-[#E7D5A8]/30 blur-3xl pointer-events-none" />

      <div className="relative max-w-3xl mx-auto px-5 sm:px-8">
        <Reveal className="text-center mb-12">
          <div className="eyebrow mb-3">Find Your Remedy</div>
          <h2 className="font-serif text-4xl sm:text-5xl text-[#0B302F] tracking-tight">
            Which flower matches your mood today?
          </h2>
          <p className="mt-4 text-[#0B302F]/70 max-w-xl mx-auto">
            Three quick questions, one gentle answer. Not a diagnosis — just a starting point for your session.
          </p>
        </Reveal>

        <div
          data-testid="remedy-quiz-card"
          className="rounded-[2rem] border border-[#EAE4DA] bg-white/80 backdrop-blur-sm shadow-sm shadow-[#0B302F]/5 p-8 sm:p-12 min-h-[360px] flex flex-col"
        >
          {step >= 0 && step < TOTAL_STEPS && (
            <div className="flex items-center gap-2 mb-8">
              {QUIZ_QUESTIONS.map((_, i) => (
                <span
                  key={i}
                  data-testid={`quiz-progress-dot-${i}`}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === step ? "w-10 bg-[#D5B876]" : i < step ? "w-6 bg-[#D5B876]/50" : "w-6 bg-[#EAE4DA]"
                  }`}
                />
              ))}
            </div>
          )}

          <AnimatePresence mode="wait">
            {step === -1 && (
              <motion.div
                key="intro"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.5, ease: EASE }}
                className="flex-1 flex flex-col items-center justify-center text-center gap-6"
              >
                <div className="h-14 w-14 rounded-full bg-[#F9EAE8] flex items-center justify-center">
                  <Sparkles className="h-6 w-6 text-[#D5B876]" strokeWidth={1.4} />
                </div>
                <p className="font-serif text-2xl sm:text-3xl text-[#0B302F] max-w-md leading-snug">
                  Ready to discover the flower essence closest to how you feel right now?
                </p>
                <Button
                  onClick={start}
                  data-testid="quiz-start-button"
                  size="lg"
                  className="rounded-full bg-[#0B302F] text-[#FDFBF7] hover:bg-[#2A5C5A] px-8 h-12 text-xs tracking-[0.22em] uppercase group"
                >
                  Start the Quiz
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
                </Button>
              </motion.div>
            )}

            {step >= 0 && step < TOTAL_STEPS && (
              <motion.div
                key={`q-${step}`}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.5, ease: EASE }}
                className="flex-1"
              >
                <div className="text-[10px] tracking-[0.24em] uppercase text-[#2A5C5A] mb-3">
                  Question {step + 1} of {TOTAL_STEPS}
                </div>
                <h3 className="font-serif text-2xl sm:text-3xl text-[#0B302F] leading-snug mb-8">
                  {QUIZ_QUESTIONS[step].prompt}
                </h3>
                <div className="grid gap-3">
                  {QUIZ_QUESTIONS[step].options.map((opt, i) => (
                    <button
                      key={i}
                      onClick={() => pick(opt.tag)}
                      data-testid={`quiz-option-${step}-${i}`}
                      className="text-left rounded-2xl border border-[#EAE4DA] bg-white px-5 py-4 text-[#0B302F]/85 hover:border-[#D5B876] hover:bg-[#F9EAE8]/40 transition-colors duration-200"
                    >
                      {opt.text}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {step === TOTAL_STEPS && result && (
              <motion.div
                key="result"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6, ease: EASE }}
                className="flex-1 flex flex-col items-center text-center gap-5"
              >
                <div className="eyebrow text-[#2A5C5A]">{result.title}</div>
                <h3 className="font-serif text-4xl sm:text-5xl text-[#0B302F] tracking-tight">
                  {topRemedy.name}
                </h3>
                <p className="text-[#0B302F]/60 text-sm italic font-serif -mt-1">{topRemedy.note}</p>
                <p className="text-[#0B302F]/75 max-w-md leading-relaxed" data-testid="quiz-result-note">
                  {QUIZ_RESULT_NOTES[result.id]}
                </p>

                <div className="mt-3 flex flex-wrap items-center justify-center gap-3">
                  <Button
                    onClick={onBookNow}
                    data-testid="quiz-book-now"
                    size="lg"
                    className="rounded-full bg-[#0B302F] text-[#FDFBF7] hover:bg-[#2A5C5A] px-7 h-12 text-xs tracking-[0.22em] uppercase"
                  >
                    Book a Session
                  </Button>
                  <a
                    href={`https://wa.me/91${PHONE}?text=${encodeURIComponent(
                      `Hello Bloom & Heal, my remedy quiz result was "${topRemedy.name}" — I'd like to know more.`
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="quiz-whatsapp"
                  >
                    <Button
                      variant="outline"
                      size="lg"
                      className="rounded-full border-[#0B302F] text-[#0B302F] hover:bg-[#F9EAE8] bg-transparent px-7 h-12 text-xs tracking-[0.22em] uppercase"
                    >
                      Ask on WhatsApp
                    </Button>
                  </a>
                  <Button
                    onClick={handleShare}
                    disabled={isSharing}
                    data-testid="quiz-share-button"
                    variant="outline"
                    size="lg"
                    className="rounded-full border-[#2A5C5A] text-[#2A5C5A] hover:bg-[#F9EAE8] bg-transparent px-7 h-12 text-xs tracking-[0.22em] uppercase disabled:opacity-60"
                  >
                    {isSharing ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" strokeWidth={1.5} />
                    ) : (
                      <Share2 className="h-4 w-4 mr-2" strokeWidth={1.5} />
                    )}
                    {isSharing ? "Preparing..." : "Share Result"}
                  </Button>
                </div>

                <button
                  onClick={retake}
                  data-testid="quiz-retake-button"
                  className="mt-2 inline-flex items-center gap-1.5 text-xs tracking-[0.18em] uppercase text-[#0B302F]/50 hover:text-[#2A5C5A] transition-colors"
                >
                  <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.5} />
                  Retake the quiz
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="mt-6 flex items-center justify-center gap-2 text-[#2A5C5A]/70 text-xs italic font-serif">
          <Leaf className="h-3.5 w-3.5 text-[#D5B876]" strokeWidth={1.4} />
          <span>A gentle guide, not a substitute for a full consultation.</span>
        </div>
      </div>

      <RemedyShareCard
        ref={shareCardRef}
        result={result}
        topRemedy={topRemedy}
        note={result ? QUIZ_RESULT_NOTES[result.id] : ""}
        instagramHandle={INSTAGRAM_HANDLE}
      />
    </section>
  );
}
